import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, LucideIcon } from "lucide-react";

export interface RichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
  required?: boolean;
}

interface ToolbarButtonProps {
  icon: LucideIcon;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ icon: Icon, title, onClick, disabled }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      // Evita que o contentEditable perca o foco/seleção antes do comando ser executado
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  onBlur,
  label,
  error,
  helperText,
  disabled,
  placeholder,
  minHeight = 160,
  required,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);

  // Mantém o conteúdo do editor sincronizado com o valor externo (ex.: aplicado pela IA),
  // sem sobrescrever o texto enquanto o usuário está digitando.
  useEffect(() => {
    const el = editorRef.current;
    if (!el || isFocusedRef.current) return;
    if ((value || "") !== el.innerHTML) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const exec = (command: string) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command);
    onChange(editorRef.current?.innerHTML || "");
  };

  const isEmpty = !value || value === "<br>";

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      <div
        className={`overflow-hidden rounded-lg border bg-white shadow-sm transition-colors dark:bg-slate-800 ${
          error
            ? "border-red-400"
            : "border-slate-300 focus-within:border-primary-500 dark:border-slate-600"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900/40">
          <ToolbarButton icon={Bold} title="Negrito" onClick={() => exec("bold")} disabled={disabled} />
          <ToolbarButton icon={Italic} title="Itálico" onClick={() => exec("italic")} disabled={disabled} />
          <ToolbarButton icon={Underline} title="Sublinhado" onClick={() => exec("underline")} disabled={disabled} />
          <span className="mx-1 h-5 w-px bg-slate-300 dark:bg-slate-600" />
          <ToolbarButton
            icon={List}
            title="Lista com marcadores"
            onClick={() => exec("insertUnorderedList")}
            disabled={disabled}
          />
          <ToolbarButton
            icon={ListOrdered}
            title="Lista numerada"
            onClick={() => exec("insertOrderedList")}
            disabled={disabled}
          />
        </div>
        <div className="relative">
          {isEmpty && placeholder && (
            <span className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400 dark:text-slate-500">
              {placeholder}
            </span>
          )}
          <div
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onFocus={() => {
              isFocusedRef.current = true;
            }}
            onBlur={() => {
              isFocusedRef.current = false;
              onBlur?.();
            }}
            onInput={() => onChange(editorRef.current?.innerHTML || "")}
            style={{ minHeight }}
            className="px-3 py-2 text-sm leading-relaxed text-slate-900 focus:outline-none dark:text-slate-100 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
          />
        </div>
      </div>
      {helperText && (
        <p className={`mt-1.5 text-xs ${error ? "text-red-500" : "text-slate-500 dark:text-slate-400"}`}>
          {helperText}
        </p>
      )}
    </div>
  );
}
